/**
 * AIMagicPanel.tsx
 *
 * AI Magic Panel - Quick AI assistant panel for the editor toolbar
 * Provides quick actions and full briefing access
 *
 * Features:
 * - Brand profile selection
 * - Quick actions: Generate, Enhance, Rewrite, Hashtags, Hook, Ideas
 * - Full briefing modal integration
 * - Loading states and error handling
 */

import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/ui/Icon';
import { CancelButton } from '@/components/ui/CancelButton';
import { Select } from '@/components/ui/Select';
import { Tooltip } from '@/components/ui/Tooltip';
import { UnsplashImagePicker, type UnsplashImage } from '@/components/ui/UnsplashImagePicker';
import { ICONS } from '@/constants/icons';
import { useAI } from '@/hooks/useAI';
import { useAIBrandProfiles } from '@/hooks/useAIBrandProfiles';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { AIBriefingModal } from '@/components/composer/AIBriefingModal';
import { AITokensExhaustedModal } from '@/components/ui/AITokensExhaustedModal';
import { useToastStore } from '@/stores/toast.store';
import { getLanguageName, SUPPORTED_LANGUAGES } from '@/utils/languages';
import { useVariationSettings } from '@/hooks/useVariationSettings';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

// ============================================================================
// Types
// ============================================================================

interface AIMagicPanelProps {
  /**
   * Panel open state
   */
  open: boolean;

  /**
   * Panel open state change handler
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Current caption text
   */
  caption: string;

  /**
   * Callback when caption is generated
   */
  onCaptionGenerated: (text: string) => void;

  /**
   * Optional callback to insert image
   */
  onInsertImage?: (url: string, credit: string, downloadUrl: string) => void;

  /**
   * Selected channel IDs for context
   */
  selectedChannels: number[];

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Topic/brief input (lifted state for persistence)
   */
  topic?: string;

  /**
   * Topic change handler (lifted state for persistence)
   */
  onTopicChange?: (topic: string) => void;

  /**
   * Image search visibility (lifted state for persistence)
   */
  showImageSearch?: boolean;

  /**
   * Image search visibility change handler (lifted state for persistence)
   */
  onShowImageSearchChange?: (show: boolean) => void;
}

type QuickAction = 'generate' | 'enhance' | 'rewrite' | 'expand' | 'shorten' | 'hashtags' | 'hook' | 'ideas' | 'images' | 'translate';

/**
 * How the accepted result should be applied to the caption.
 * - replace: overwrite the caption with the result (default behavior)
 * - prepend: put the result before the existing caption (Hook)
 * - copy: copy the result to the clipboard, leave the caption untouched (Ideas)
 */
type AcceptMode = 'replace' | 'prepend' | 'copy';

interface ActionOutcome {
  result: string | null;
  tokens?: number;
  acceptMode: AcceptMode;
}

interface ActionGuardError {
  guardError: string;
}

type ExecuteActionResult = ActionOutcome | ActionGuardError;

const isGuardError = (outcome: ExecuteActionResult): outcome is ActionGuardError =>
  'guardError' in outcome;

/** Default instruction used by Generate when there's a draft but no topic/box. */
const DEFAULT_GENERATE_INSTRUCTION =
  'Rewrite and polish this into a stronger, more engaging version while keeping its meaning.';

/** Preset instructions for the draft-required transform actions. */
const PRESET_INSTRUCTIONS: Record<'enhance' | 'rewrite' | 'expand' | 'shorten', string> = {
  enhance:
    'Improve this caption: fix grammar, sharpen the wording, and make it more engaging — keep roughly the same length and the original meaning.',
  rewrite: 'Rewrite this caption with fresh wording and a different structure, keeping the core message.',
  expand: 'Expand this caption with more detail, examples, and depth. Make it noticeably longer.',
  shorten: 'Make this caption shorter and more concise while keeping the key message.',
};

// ============================================================================
// Component
// ============================================================================

export const AIMagicPanel = ({
  open: _open, // Kept for potential future use (parent controls visibility)
  onOpenChange,
  caption,
  onCaptionGenerated,
  onInsertImage,
  selectedChannels,
  disabled = false,
  topic: externalTopic,
  onTopicChange,
  showImageSearch: externalShowImageSearch,
  onShowImageSearchChange,
}: AIMagicPanelProps): JSX.Element | null => {
  const { t } = useTranslation('ai');

  // Brand profiles — DRAFT profiles aren't selectable for generation, only ACTIVE ones.
  const { profiles, defaultProfile, isLoading: profilesLoading } = useAIBrandProfiles();
  const activeProfiles = profiles.filter((profile) => profile.status === 'ACTIVE');
  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(
    defaultProfile?.id
  );
  // Tracks whether the user has explicitly picked (or cleared) a profile, so the
  // async-default sync below never clobbers a manual choice.
  const userTouchedProfileRef = useRef(false);

  // Sync selectedProfileId to the default profile once it resolves (useAIBrandProfiles
  // is async), but only if the user hasn't touched the selector yet.
  useEffect(() => {
    if (userTouchedProfileRef.current) return;
    if (defaultProfile && selectedProfileId === undefined) {
      setSelectedProfileId(defaultProfile.id);
    }
  }, [defaultProfile, selectedProfileId]);

  // brand_world locked by the plan: no brand profile is applied or sent (existing profiles stay visible elsewhere)
  const brandWorldLock = useFeatureLock('brand_world');
  const effectiveProfileId = brandWorldLock.locked ? undefined : selectedProfileId;

  // Topic/brief input - use external state if provided, otherwise local
  const [localTopic, setLocalTopic] = useState('');
  const topic = externalTopic ?? localTopic;
  const setTopic = onTopicChange ?? setLocalTopic;

  // Loading state for quick actions
  const [loadingAction, setLoadingAction] = useState<QuickAction | null>(null);

  // Full briefing modal
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);

  // Generated result preview
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [resultMeta, setResultMeta] = useState<{ tokens?: number; timestamp?: string; action?: string } | null>(null);
  const [lastAction, setLastAction] = useState<QuickAction | null>(null);
  const [acceptMode, setAcceptMode] = useState<AcceptMode>('replace');

  // Mobile toggle view: 'main' shows the action panel, 'result' shows the generated result
  const [mobileView, setMobileView] = useState<'main' | 'result'>('main');

  // Ref + state for measuring the Magic Ed header height (desktop layout alignment)
  const magicHeaderRef = useRef<HTMLDivElement>(null);
  const [magicHeaderHeight, setMagicHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    const el = magicHeaderRef.current;
    if (!el) return;
    const update = () => setMagicHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const magicFooterRef = useRef<HTMLDivElement>(null);
  const [magicFooterHeight, setMagicFooterHeight] = useState(0);

  useLayoutEffect(() => {
    const el = magicFooterRef.current;
    if (!el) return;
    const update = () => setMagicFooterHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [generatedResult]);

  // Image search toggle - use external state if provided, otherwise local
  const [localShowImageSearch, setLocalShowImageSearch] = useState(false);
  const showImageSearch = externalShowImageSearch !== undefined ? externalShowImageSearch : localShowImageSearch;
  const setShowImageSearch = onShowImageSearchChange ?? setLocalShowImageSearch;

  // More actions toggle - persisted in localStorage
  const [showMoreActions, setShowMoreActions] = useState(() => {
    try {
      return localStorage.getItem('magic-ed-expanded') === 'true';
    } catch {
      return false;
    }
  });

  // Translate language picker
  const [showTranslateMenu, setShowTranslateMenu] = useState(false);
  const [lastTranslateLanguage, setLastTranslateLanguage] = useState<string | null>(null);

  // Refs for scroll-into-view on section open
  const translateSectionRef = useRef<HTMLDivElement>(null);
  const imageSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to section when it opens
  useEffect(() => {
    if (showTranslateMenu && translateSectionRef.current) {
      setTimeout(() => {
        translateSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [showTranslateMenu]);

  useEffect(() => {
    if (showImageSearch && imageSectionRef.current) {
      setTimeout(() => {
        imageSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [showImageSearch]);

  // Get team's configured variation languages
  const { teamId: effectiveTeamId } = useEffectiveTeamId();
  const { data: variationSettings } = useVariationSettings(effectiveTeamId ?? 0);

  // AI hook
  const { generateCaption, improveContent, generateHashtags, translateCaption, isErrorHandled, isTokensExhaustedError, tokensExhausted, dismissTokensExhausted } = useAI();

  // Toast hook
  const { addToast } = useToastStore();

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Handle toggle more actions
   */
  const toggleMoreActions = useCallback(() => {
    setShowMoreActions(prev => {
      const next = !prev;
      try { localStorage.setItem('magic-ed-expanded', String(next)); } catch {}
      return next;
    });
  }, []);

  /**
   * Handle image selection from UnsplashImagePicker
   */
  const handleSelectImage = useCallback(
    (image: UnsplashImage) => {
      if (onInsertImage) {
        onInsertImage(
          image.url,
          `Photo by ${image.photographer}`,
          image.downloadUrl
        );
        addToast({ type: 'success', message: t('ai_magic_panel_image_added', { defaultValue: 'Image added to post' }), duration: 3000 });
      }
    },
    [onInsertImage, addToast, t]
  );

  /**
   * Single shared executor for all steerable actions (Generate, Enhance, Rewrite,
   * Expand, Shorten, Hashtags, Hook, Ideas). Owns routing, guards, instruction
   * strings, and the resulting acceptMode. `translate` and `images` are handled
   * outside the executor (dedicated flows), so those branches are unreachable in
   * practice but kept for exhaustiveness.
   *
   * `handleQuickAction` and `handleTryAgain` are thin wrappers around this —
   * they can never diverge again.
   */
  const executeAction = useCallback(
    async (action: QuickAction): Promise<ExecuteActionResult> => {
      const box = topic.trim();
      const draft = caption.trim();

      switch (action) {
        case 'generate': {
          if (!draft && !box) {
            return { guardError: t('ai_magic_panel_topic_or_draft_required') };
          }

          if (!draft) {
            const response = await generateCaption({
              topic: box,
              tone: 'friendly',
              length: 'medium',
              channelIds: selectedChannels,
              brandProfileId: effectiveProfileId,
            });
            return {
              result: response?.content || null,
              tokens: response?.usage?.tokens,
              acceptMode: 'replace',
            };
          }

          const instruction = box || DEFAULT_GENERATE_INSTRUCTION;
          const response = await improveContent({
            content: draft,
            instruction,
            brandProfileId: effectiveProfileId,
          });
          return {
            result: response?.content || null,
            tokens: response?.usage?.tokens,
            acceptMode: 'replace',
          };
        }

        case 'enhance':
        case 'rewrite':
        case 'expand':
        case 'shorten': {
          if (!draft) {
            return { guardError: t('ai_magic_panel_content_required') };
          }

          const preset = PRESET_INSTRUCTIONS[action];
          const instruction = box ? `${preset} Additional direction from the user: ${box}` : preset;
          const response = await improveContent({
            content: draft,
            instruction,
            brandProfileId: effectiveProfileId,
          });
          return {
            result: response?.content || null,
            tokens: response?.usage?.tokens,
            acceptMode: 'replace',
          };
        }

        case 'hashtags': {
          if (!draft) {
            return { guardError: t('ai_magic_panel_content_required') };
          }

          const hashtags = await generateHashtags(caption, undefined, undefined, effectiveProfileId);
          const result = hashtags && hashtags.length > 0 ? `${draft}\n\n${hashtags.join(' ')}` : null;
          return { result, acceptMode: 'replace' };
        }

        case 'hook': {
          if (!draft && !box) {
            return { guardError: t('ai_magic_panel_topic_or_draft_required') };
          }

          let hookTopic: string;
          let hookAcceptMode: AcceptMode;
          if (!draft) {
            hookTopic = `Write ONE attention-grabbing opening hook line for a post about: ${box}`;
            hookAcceptMode = 'replace';
          } else if (!box) {
            hookTopic = `Write ONE attention-grabbing opening hook line for this post:\n\n${draft}`;
            hookAcceptMode = 'prepend';
          } else {
            hookTopic = `Following this direction: "${box}", write ONE attention-grabbing opening hook line for this post:\n\n${draft}`;
            hookAcceptMode = 'prepend';
          }

          const response = await generateCaption({
            topic: hookTopic,
            tone: 'friendly',
            length: 'short',
            channelIds: selectedChannels,
            brandProfileId: effectiveProfileId,
          });
          return {
            result: response?.content || null,
            tokens: response?.usage?.tokens,
            acceptMode: hookAcceptMode,
          };
        }

        case 'ideas': {
          if (!draft && !box) {
            return { guardError: t('ai_magic_panel_topic_or_draft_required') };
          }

          let ideasTopic: string;
          if (!draft) {
            ideasTopic = `Generate 3 distinct content ideas for: ${box}`;
          } else if (!box) {
            ideasTopic = `Generate 3 distinct content ideas based on this post:\n\n${draft}`;
          } else {
            ideasTopic = `Following this direction: "${box}", generate 3 distinct content ideas based on this post:\n\n${draft}`;
          }

          const response = await generateCaption({
            topic: ideasTopic,
            tone: 'professional',
            length: 'medium',
            channelIds: selectedChannels,
            brandProfileId: effectiveProfileId,
          });
          return {
            result: response?.content || null,
            tokens: response?.usage?.tokens,
            acceptMode: 'copy',
          };
        }

        case 'translate':
        case 'images':
          // Handled outside the executor (dedicated translate flow / panel toggle).
          return { guardError: t('ai_magic_panel_failed') };
      }
    },
    [topic, caption, selectedChannels, effectiveProfileId, generateCaption, improveContent, generateHashtags, t]
  );

  /**
   * Handle quick action clicks — thin wrapper around executeAction.
   */
  const handleQuickAction = async (action: QuickAction): Promise<void> => {
    // Also guard against an in-flight "Try Again" (isRetrying), which doesn't
    // set loadingAction — otherwise a quick-action click can race a retry and
    // both write generatedResult concurrently.
    if (disabled || loadingAction || isRetrying) return;

    // Panel toggles — no async action, not routed through the executor.
    if (action === 'images') {
      setShowImageSearch(!showImageSearch);
      return;
    }
    if (action === 'translate') {
      setShowTranslateMenu(!showTranslateMenu);
      return;
    }

    setLoadingAction(action);

    try {
      const outcome = await executeAction(action);

      if (isGuardError(outcome)) {
        addToast({ type: 'error', message: outcome.guardError, duration: 5000 });
        return;
      }

      if (outcome.result) {
        setGeneratedResult(outcome.result);
        setResultMeta({ tokens: outcome.tokens, timestamp: new Date().toISOString(), action });
        setLastAction(action);
        setAcceptMode(outcome.acceptMode);
        // Don't insert yet — let user preview and confirm
      } else if (!isErrorHandled() && !isTokensExhaustedError()) {
        addToast({ type: 'error', message: t('ai_magic_panel_failed'), duration: 5000 });
      }
    } catch {
      addToast({ type: 'error', message: t('ai_magic_panel_error'), duration: 5000 });
    } finally {
      setLoadingAction(null);
    }
  };

  /**
   * Handle translate action - translate caption to selected language.
   * Kept as a dedicated flow (needs a target-language argument the executor
   * doesn't carry) but shares the same accept-flow as every other action.
   */
  const handleTranslate = async (targetLanguage: string): Promise<void> => {
    if (disabled || loadingAction) return;

    if (!caption.trim()) {
      addToast({ type: 'error', message: t('ai_magic_panel_content_required'), duration: 5000 });
      return;
    }

    setLoadingAction('translate');
    setShowTranslateMenu(false);
    setLastTranslateLanguage(targetLanguage);

    try {
      const response = await translateCaption(caption, targetLanguage);
      if (response?.content) {
        setGeneratedResult(response.content);
        setResultMeta({ tokens: response.usage?.tokens, timestamp: new Date().toISOString(), action: 'translate' });
        setLastAction('translate');
        setAcceptMode('replace');
      } else if (!isErrorHandled() && !isTokensExhaustedError()) {
        addToast({ type: 'error', message: t('ai_magic_panel_failed'), duration: 5000 });
      }
    } catch {
      addToast({ type: 'error', message: t('ai_magic_panel_error'), duration: 5000 });
    } finally {
      setLoadingAction(null);
    }
  };

  /**
   * Accept the generated result — branches on acceptMode:
   * - replace: insert the result into the caption (default behavior)
   * - prepend: put the result before the current draft (Hook)
   * - copy: copy to clipboard, leave the caption untouched (Ideas)
   */
  const handleAcceptResult = async (): Promise<void> => {
    if (!generatedResult) return;

    if (acceptMode === 'copy') {
      try {
        if (!navigator.clipboard?.writeText) {
          throw new Error('clipboard unavailable');
        }
        await navigator.clipboard.writeText(generatedResult);
        addToast({ type: 'success', message: t('ai_magic_panel_copied'), duration: 3000 });
      } catch {
        addToast({ type: 'error', message: t('ai_magic_panel_copy_failed'), duration: 4000 });
      } finally {
        // Always clear + close, even if the clipboard write failed — the panel
        // must never get stuck on a non-secure-context / permission failure.
        setGeneratedResult(null);
        setLastAction(null);
        setAcceptMode('replace');
        onOpenChange(false);
      }
      return;
    }

    if (acceptMode === 'prepend') {
      const combined = caption.trim().length > 0 ? `${generatedResult}\n\n${caption}` : generatedResult;
      onCaptionGenerated(combined);
      setGeneratedResult(null);
      setLastAction(null);
      setAcceptMode('replace');
      onOpenChange(false);
      return;
    }

    onCaptionGenerated(generatedResult);
    setGeneratedResult(null);
    setLastAction(null);
    setAcceptMode('replace');
    onOpenChange(false);
  };

  /**
   * Discard result and go back to panel
   */
  const handleDiscardResult = (): void => {
    setGeneratedResult(null);
    setResultMeta(null);
    setLastAction(null);
    setAcceptMode('replace');
    setMobileView('main');
  };

  /**
   * Try again with the same action — keeps panel open with loading state.
   * Thin wrapper around executeAction (translate reuses its own dedicated call
   * since it needs lastTranslateLanguage). No second switch statement.
   */
  const [isRetrying, setIsRetrying] = useState(false);

  const handleTryAgain = async (): Promise<void> => {
    const action = lastAction;
    // Also guard against an in-flight quick action (loadingAction) or an
    // already-running retry, so this can never race handleQuickAction.
    if (!action || loadingAction || isRetrying) return;

    setIsRetrying(true);
    try {
      if (action === 'translate') {
        if (!lastTranslateLanguage) {
          addToast({ type: 'error', message: t('ai_magic_panel_failed'), duration: 5000 });
          return;
        }
        const response = await translateCaption(caption, lastTranslateLanguage);
        if (response?.content) {
          setGeneratedResult(response.content);
          setResultMeta({ tokens: response.usage?.tokens, timestamp: new Date().toISOString(), action });
          setAcceptMode('replace');
        } else if (!isErrorHandled() && !isTokensExhaustedError()) {
          addToast({ type: 'error', message: t('ai_magic_panel_failed'), duration: 5000 });
        }
        return;
      }

      const outcome = await executeAction(action);

      if (isGuardError(outcome)) {
        addToast({ type: 'error', message: outcome.guardError, duration: 5000 });
        return;
      }

      if (outcome.result) {
        setGeneratedResult(outcome.result);
        setResultMeta({ tokens: outcome.tokens, timestamp: new Date().toISOString(), action });
        setAcceptMode(outcome.acceptMode);
      } else if (!isErrorHandled() && !isTokensExhaustedError()) {
        addToast({ type: 'error', message: t('ai_magic_panel_failed'), duration: 5000 });
      }
    } catch {
      addToast({ type: 'error', message: t('ai_magic_panel_error'), duration: 5000 });
    } finally {
      setIsRetrying(false);
    }
  };

  /**
   * Handle full briefing completion
   */
  const handleBriefingComplete = (text: string): void => {
    onCaptionGenerated(text);
    setBriefingModalOpen(false);
    onOpenChange(false);
  };

  // Auto-switch to result view on mobile when a result is generated
  useEffect(() => {
    if (generatedResult && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setMobileView('result');
    }
    if (!generatedResult) {
      setMobileView('main');
    }
  }, [generatedResult]);

  // Clear result preview when panel closes
  useEffect(() => {
    if (!_open) {
      setGeneratedResult(null);
      setResultMeta(null);
      setLastAction(null);
      setAcceptMode('replace');
    }
  }, [_open]);

  // Close on Escape key
  useEffect(() => {
    if (!_open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [_open, onOpenChange]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* Mobile: sliding viewport | Desktop: side-by-side flex */}
      <div className="relative h-full lg:h-[min(550px,calc(100vh-2rem))] overflow-hidden w-full max-w-[100vw] lg:w-auto lg:max-w-none lg:rounded-xl lg:shadow-2xl">
      <div
        className={cn(
          "flex items-stretch gap-0 h-full lg:h-full",
          "w-[200%] lg:w-auto",
          "transition-transform duration-300 ease-out lg:transition-none lg:!translate-x-0",
          mobileView === 'result' ? '-translate-x-1/2' : 'translate-x-0'
        )}
        style={{ '--magic-header-h': `${magicHeaderHeight}px`, '--magic-footer-h': `${magicFooterHeight}px` } as React.CSSProperties}
      >
      {/* Main Panel */}
      <div className={cn(
        "relative w-1/2 lg:w-96 lg:h-full flex flex-col bg-white border border-gray-200 overflow-hidden flex-shrink-0",
        "transition-all duration-300",
        generatedResult ? "rounded-none lg:rounded-xl lg:rounded-r-none border-r-0" : "rounded-none lg:rounded-xl"
      )}>
        {/* Header - Fixed */}
        <div ref={magicHeaderRef} className="flex-shrink-0 bg-gradient-to-r from-primary-50 to-soft-mint px-4 py-3 border-b border-gray-200">
          <div className="flex items-start gap-3">
            {/* Magic Ed Image */}
            <img
              src="/images/magic-ed.png"
              alt="Magic Ed"
              className="w-16 h-16 rounded-full object-cover flex-shrink-0"
            />

            {/* View result arrow — mobile only, when result exists */}
            {generatedResult && (
              <button
                type="button"
                onClick={() => setMobileView('result')}
                className="lg:hidden absolute right-3 top-1.5 w-8 h-8 flex items-center justify-center text-emerald-600 hover:text-emerald-700 transition-colors"
                aria-label="View result"
              >
                <Icon name={ICONS.ANGLE_RIGHT} size="sm" ariaHidden />
              </button>
            )}

            {/* Title and Controls */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">
                {t('ai_briefing_magic_ed_label')}
              </h3>

              {/* Brand Profile Selector with Settings Icon */}
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  {brandWorldLock.locked ? (
                    <button
                      type="button"
                      onClick={brandWorldLock.showUpsell}
                      data-testid="brand-profile-locked"
                      className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white/60 px-3 py-1.5 text-left text-xs font-medium text-gray-500 transition-colors hover:border-gray-300 focus:outline-none focus-visible:border-primary-600"
                    >
                      <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                      <span className="truncate">{brandWorldLock.label}</span>
                    </button>
                  ) : (
                  <Select
                    value={selectedProfileId?.toString() || ''}
                    onValueChange={(val) => {
                      userTouchedProfileRef.current = true;
                      setSelectedProfileId(val ? Number(val) : undefined);
                    }}
                    disabled={profilesLoading || disabled}
                    placeholder={profilesLoading
                      ? t('ai_magic_panel_loading_profiles')
                      : t('ai_magic_panel_no_profile')}
                    options={activeProfiles.map((profile) => ({
                      value: profile.id.toString(),
                      label: profile.name + (profile.isDefault ? ` (${t('ai_magic_panel_default')})` : ''),
                    }))}
                    clearable
                    clearLabel={t('ai_magic_panel_no_profile')}
                    size="sm"
                  />
                  )}
                </div>
                <Tooltip content={t('ai_magic_panel_manage_profiles')} side="bottom">
                  <a
                    href="/settings?tab=ai"
                    className="p-1.5 rounded-md hover:bg-white/50 transition-colors flex-shrink-0"
                  >
                    <Icon name={ICONS.COG} size="sm" color="text-gray-600" />
                  </a>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin pb-4">
        {/* Optional topic hint */}
        <div className="px-4 py-3 border-b border-gray-200">
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={caption.trim().length === 0 ? t('ai_magic_panel_topic_for_generate') : t('ai_magic_panel_topic_for_enhance')}
            disabled={disabled}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed resize-y max-h-32"
          />
          <p className="mt-1 text-xs text-gray-400">
            {t('ai_magic_panel_topic_hint_extended')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs font-medium text-gray-700 mb-2">
            {t('ai_magic_panel_quick_actions')}
          </p>

          {/* Sparkle Loading Overlay — covers entire panel */}
          {loadingAction && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80 backdrop-blur-[1px] rounded-xl overflow-hidden">
              {/* Floating sparkles */}
              {[...Array(12)].map((_, i) => (
                <span
                  key={i}
                  className="absolute text-primary-400 animate-pulse"
                  style={{
                    left: `${10 + (i * 7) % 80}%`,
                    top: `${5 + (i * 13) % 85}%`,
                    fontSize: `${8 + (i % 4) * 4}px`,
                    animationDelay: `${i * 150}ms`,
                    animationDuration: `${1200 + (i % 3) * 400}ms`,
                    opacity: 0.3 + (i % 4) * 0.2,
                  }}
                >
                  ✦
                </span>
              ))}
              {/* Center content */}
              <div className="relative z-10 flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-200 animate-pulse">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: '22px' }}>auto_awesome</span>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {loadingAction === 'generate' && t('ai_magic_panel_generating', { defaultValue: 'Generating caption...' })}
                  {loadingAction === 'enhance' && t('ai_magic_panel_enhancing', { defaultValue: 'Enhancing...' })}
                  {loadingAction === 'rewrite' && t('ai_magic_panel_rewriting', { defaultValue: 'Rewriting...' })}
                  {loadingAction === 'expand' && t('ai_magic_panel_expanding', { defaultValue: 'Expanding...' })}
                  {loadingAction === 'shorten' && t('ai_magic_panel_shortening', { defaultValue: 'Shortening...' })}
                  {loadingAction === 'hashtags' && t('ai_magic_panel_generating_hashtags', { defaultValue: 'Generating hashtags...' })}
                  {loadingAction === 'hook' && t('ai_magic_panel_generating_hook', { defaultValue: 'Creating hook...' })}
                  {loadingAction === 'ideas' && t('ai_magic_panel_generating_ideas', { defaultValue: 'Generating ideas...' })}
                  {loadingAction === 'translate' && t('ai_magic_panel_translating', { defaultValue: 'Translating...' })}
                </span>
                <span className="text-xs text-gray-400">{t('ai_magic_panel_please_wait', { defaultValue: 'Magic Ed is working...' })}</span>
              </div>
            </div>
          )}

          {/* Single unified grid with conditional rendering */}
          {(() => {
            // Define all actions as objects
            const allActions: Array<{
              action: QuickAction;
              icon: string;
              label: string;
              tooltip: string;
              isActive?: boolean;
            }> = [
              // Primary creative actions (always visible - top 3)
              { action: 'generate', icon: ICONS.SPARKLES, label: t('ai_magic_panel_generate'), tooltip: t('ai_magic_panel_generate_tooltip') },
              { action: 'enhance', icon: ICONS.BULB, label: t('ai_magic_panel_enhance'), tooltip: t('ai_magic_panel_enhance_tooltip') },
              { action: 'rewrite', icon: ICONS.REFRESH, label: t('ai_magic_panel_rewrite'), tooltip: t('ai_magic_panel_rewrite_tooltip') },
              // Secondary actions (collapsed)
              { action: 'shorten', icon: ICONS.MINUS, label: t('ai_magic_panel_shorten', { defaultValue: 'Shorten' }), tooltip: t('ai_magic_panel_shorten_tooltip', { defaultValue: 'Make it shorter' }) },
              { action: 'expand', icon: ICONS.PLUS, label: t('ai_magic_panel_expand', { defaultValue: 'Expand' }), tooltip: t('ai_magic_panel_expand_tooltip', { defaultValue: 'Expand with more detail' }) },
              { action: 'hashtags', icon: ICONS.TAG, label: t('ai_magic_panel_hashtags', { defaultValue: 'Hashtags' }), tooltip: t('ai_magic_panel_hashtags_tooltip', { defaultValue: 'Generate hashtags' }) },
              { action: 'hook', icon: ICONS.PAPER_PLANE, label: t('ai_magic_panel_hook'), tooltip: t('ai_magic_panel_hook_tooltip') },
              { action: 'ideas', icon: ICONS.BULB, label: t('ai_magic_panel_ideas'), tooltip: t('ai_magic_panel_ideas_tooltip') },
              { action: 'images', icon: ICONS.IMAGE, label: t('ai_magic_panel_images', { defaultValue: 'Images' }), tooltip: t('ai_magic_panel_images_tooltip', { defaultValue: 'Search free images' }), isActive: showImageSearch },
              { action: 'translate', icon: ICONS.LANGUAGE, label: t('ai_magic_panel_translate', { defaultValue: 'Translate' }), tooltip: t('ai_magic_panel_translate_tooltip', { defaultValue: 'Translate to another language' }), isActive: showTranslateMenu },
            ];

            const VISIBLE_COUNT = 3;
            const hiddenCount = allActions.length - VISIBLE_COUNT;

            // Primary actions (always visible)
            const primaryActions = allActions.slice(0, VISIBLE_COUNT);
            const secondaryActions = allActions.slice(VISIBLE_COUNT);

            return (
              <div className="grid grid-cols-3 gap-1.5">
                {/* Primary actions - always visible */}
                {primaryActions.map(({ action, icon, label, tooltip, isActive }) => (
                  <Tooltip key={action} content={tooltip} side="bottom">
                    <button
                      type="button"
                      onClick={() => handleQuickAction(action)}
                      disabled={disabled || loadingAction !== null}
                      className={cn(
                        'flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg border transition-all',
                        loadingAction === action || isActive
                          ? 'bg-primary-100 border-primary-300'
                          : 'bg-white border-gray-200 hover:border-primary-300 hover:bg-primary-50',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      <Icon
                        name={icon}
                        size="sm"
                        color={loadingAction === action || isActive ? 'text-primary-600' : 'text-gray-600'}
                      />
                      <span className="text-xs font-medium text-gray-700">
                        {label}
                      </span>
                    </button>
                  </Tooltip>
                ))}

                {/* Secondary actions - animated expand/collapse */}
                {secondaryActions.map(({ action, icon, label, tooltip, isActive }, index) => (
                  <div
                    key={action}
                    className={cn(
                      'transition-all duration-200 ease-out',
                      showMoreActions
                        ? 'opacity-100 translate-y-0 scale-100'
                        : 'opacity-0 translate-y-2 scale-95 h-0 overflow-hidden pointer-events-none'
                    )}
                    style={{ transitionDelay: showMoreActions ? `${index * 30}ms` : '0ms' }}
                  >
                    <Tooltip content={tooltip} side="bottom">
                      <button
                        type="button"
                        onClick={() => handleQuickAction(action)}
                        disabled={disabled || loadingAction !== null}
                        className={cn(
                          'w-full flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg border transition-all',
                          loadingAction === action || isActive
                            ? 'bg-primary-100 border-primary-300'
                            : 'bg-white border-gray-200 hover:border-primary-300 hover:bg-primary-50',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        <Icon
                          name={icon}
                          size="sm"
                          color={loadingAction === action || isActive ? 'text-primary-600' : 'text-gray-600'}
                        />
                        <span className="text-xs font-medium text-gray-700">
                          {label}
                        </span>
                      </button>
                    </Tooltip>
                  </div>
                ))}

                {/* Toggle button - always last */}
                <button
                  type="button"
                  onClick={toggleMoreActions}
                  className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:border-primary-300 hover:bg-primary-50 transition-all text-gray-500 hover:text-gray-700"
                >
                  <span className={cn('text-sm font-medium transition-transform duration-200', showMoreActions && 'rotate-45')}>
                    +{showMoreActions ? '' : hiddenCount}
                  </span>
                </button>
              </div>
            );
          })()}
        </div>

        {/* Image Search Section */}
        {showImageSearch && (
          <div ref={imageSectionRef} className="px-4 border-t border-gray-200 pt-3">
            <UnsplashImagePicker
              onSelectImage={handleSelectImage}
              disabled={disabled}
              size="sm"
            />
          </div>
        )}

        {/* Translate Language Picker - inline section like Image Search */}
        {showTranslateMenu && (
          <div ref={translateSectionRef} className="px-4 border-t border-gray-200 pt-3 pb-1">
            {/* Team's configured languages */}
            {variationSettings?.variationLanguages && variationSettings.variationLanguages.length > 0 && (
              <>
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">
                  {t('ai_magic_panel_team_languages', { defaultValue: 'Team Languages' })}
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {variationSettings.variationLanguages.map((lang: string) => (
                    <button
                      key={`team-${lang}`}
                      type="button"
                      onClick={() => handleTranslate(lang.toUpperCase())}
                      disabled={disabled || loadingAction !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {getLanguageName(lang)}
                    </button>
                  ))}
                </div>
              </>
            )}
            {/* All languages */}
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">
              {t('ai_magic_panel_all_languages', { defaultValue: 'All Languages' })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SUPPORTED_LANGUAGES
                .filter((lang) => !(variationSettings?.variationLanguages || []).map((l: string) => l.toUpperCase()).includes(lang))
                .map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => handleTranslate(lang)}
                    disabled={disabled || loadingAction !== null}
                    className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {getLanguageName(lang)}
                  </button>
                ))}
            </div>
          </div>
        )}

        </div>{/* End Scrollable Content */}

        {/* Full Briefing + Close — fixed footer */}
        <div
          className="flex-shrink-0 px-4 py-2 border-t border-gray-200 flex items-center gap-1.5 sm:gap-2"
          style={{ minHeight: `var(--magic-footer-h, auto)` }}
        >
          <CancelButton onClick={() => onOpenChange(false)} tooltip="Close" className="!w-9 !h-9 !rounded-lg" />
          <button
            type="button"
            onClick={() => setBriefingModalOpen(true)}
            disabled={disabled}
            className="flex-1 h-9 px-3 text-sm bg-primary-500 hover:bg-primary-600 text-primary-contrast font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name={ICONS.SPARKLES} size="xs" className="inline-block mr-1.5" />
            {t('ai_magic_panel_full_briefing')}
          </button>
        </div>
      </div>{/* End Main Panel */}

      {/* Result Preview Card — second panel in sliding viewport */}
      <div
        className={cn(
          "flex flex-col lg:h-full bg-white border border-gray-200 overflow-hidden flex-shrink-0",
          "w-1/2 lg:w-96 xl:w-[28rem] 2xl:w-[32rem]",
          "lg:transition-all lg:duration-300 lg:ease-out",
          generatedResult
            ? "lg:opacity-100 lg:translate-x-0 rounded-none lg:rounded-xl lg:rounded-l-none lg:border-l-0"
            : "lg:hidden rounded-none lg:rounded-xl lg:rounded-l-none lg:border-l-0"
        )}
      >
        {generatedResult && (
          <>
            {/* Result Header — matches left panel header height on desktop */}
            <div
              className="flex-shrink-0 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-3 border-b border-gray-200 flex flex-col items-center justify-center"
              style={{ minHeight: `var(--magic-header-h, auto)` }}
            >
              <div className="flex items-center gap-2">
                {/* Back to Magic Ed — mobile only */}
                <button
                  type="button"
                  onClick={() => setMobileView('main')}
                  className="lg:hidden w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/50 transition-colors text-gray-600"
                  aria-label="Back to Magic Ed"
                >
                  <Icon name={ICONS.ARROW_LEFT} size="sm" ariaHidden />
                </button>
                <span
                  className="material-symbols-outlined text-emerald-600 leading-none"
                  style={{ fontSize: '18px' }}
                >
                  auto_awesome
                </span>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('ai_magic_panel_result_title', { defaultValue: 'Generated Result' })}
                </h3>
              </div>

              {/* Metadata */}
              {resultMeta && (
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-[10px] text-gray-500">
                  {resultMeta.action && (
                    <span className="inline-flex items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>auto_awesome</span>
                      <span className="capitalize">{resultMeta.action.replace('_', ' ')}</span>
                    </span>
                  )}
                  {resultMeta.tokens && (
                    <span className="inline-flex items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>token</span>
                      {resultMeta.tokens.toLocaleString()} {t('tokens', { defaultValue: 'tokens' })}
                    </span>
                  )}
                  {resultMeta.timestamp && (
                    <span className="inline-flex items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>schedule</span>
                      {new Date(resultMeta.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {generatedResult && (
                    <span className="inline-flex items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>text_fields</span>
                      {generatedResult.length} {t('chars', { defaultValue: 'chars' })}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Result Content — scrollable */}
            <div className="relative flex-1 min-h-0 overflow-y-auto p-4">
              <div className={cn(
                'bg-gray-50 rounded-lg p-3 border border-gray-100 transition-opacity duration-200',
                isRetrying && 'opacity-40'
              )}>
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {generatedResult}
                </p>
              </div>
              {isRetrying && (
                <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                  {[...Array(6)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute text-primary-400 animate-pulse"
                      style={{
                        left: `${15 + (i * 12) % 70}%`,
                        top: `${10 + (i * 17) % 75}%`,
                        fontSize: `${8 + (i % 3) * 3}px`,
                        animationDelay: `${i * 200}ms`,
                        animationDuration: `${1000 + (i % 3) * 300}ms`,
                        opacity: 0.4 + (i % 3) * 0.2,
                      }}
                    >
                      ✦
                    </span>
                  ))}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 rounded-full shadow-sm border border-gray-200">
                    <span className="material-symbols-outlined text-primary-500 animate-spin" style={{ fontSize: '14px' }}>auto_awesome</span>
                    <span className="text-xs text-gray-600">{t('ai_magic_panel_regenerating', { defaultValue: 'Regenerating...' })}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Result Actions */}
            <div ref={magicFooterRef} className="flex-shrink-0 px-4 py-3 border-t border-gray-200 space-y-2">
              <button
                type="button"
                onClick={handleAcceptResult}
                disabled={isRetrying}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name={acceptMode === 'copy' ? ICONS.COPY : ICONS.CHECK} size="sm" />
                {acceptMode === 'copy'
                  ? t('ai_magic_panel_copy', { defaultValue: 'Copy' })
                  : t('ai_magic_panel_use_this', { defaultValue: 'Use This' })}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleTryAgain()}
                  disabled={isRetrying}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name={isRetrying ? ICONS.SPINNER : ICONS.REFRESH} size="xs" className={isRetrying ? 'animate-spin' : ''} />
                  {isRetrying ? t('ai_magic_panel_regenerating', { defaultValue: 'Regenerating...' }) : t('ai_magic_panel_try_again', { defaultValue: 'Try Again' })}
                </button>
                <button
                  type="button"
                  onClick={handleDiscardResult}
                  disabled={isRetrying}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name={ICONS.CROSS} size="xs" />
                  {t('ai_magic_panel_discard', { defaultValue: 'Discard' })}
                </button>
              </div>
            </div>
          </>
        )}
      </div>{/* End Result Preview Card */}

      </div>{/* End sliding inner flex */}
      </div>{/* End sliding viewport */}

      {/* Full Briefing Modal */}
      <AIBriefingModal
        isOpen={briefingModalOpen}
        onClose={() => setBriefingModalOpen(false)}
        onInsertCaption={handleBriefingComplete}
        onInsertImage={onInsertImage}
      />

      {/* AI Tokens Exhausted Modal */}
      <AITokensExhaustedModal
        open={tokensExhausted}
        onClose={dismissTokensExhausted}
      />
    </>
  );
};

export default AIMagicPanel;
